import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Spin, Card, Row, Tabs, Table, Button, DatePicker, Empty, Pagination } from 'antd';
import moment from 'moment';
import SurveyBarChart from '../../components/Chart/SurveyChart';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const MONTH_NAMES = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];
const THAI_YEAR_OFFSET = 543;
const UNUSED_QUESTION_PHRASES = ['เลือกได้มากกว่า 1 ข้อ', 'เพศ', 'ภูมิลำเนา', 'ช่วงอายุ', 'อุปกรณ์ที่ใช้', 'ผู้ใช้บริการโทรเวชกรรม (Telemedicine)'];

const DataCard = styled(Card)`
  height: 100%;
  width: 400px;
  min-width: 250px;
  cursor: pointer;
  border-radius: 5px;
  border: 0.5px solid #d3d3d3;
  &:hover {
    transform: scale(1.08);
    z-index: 999;
  }
  transition: 0.3s;
`;

const View = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h5`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
`;

const Description = styled.p`
  font-size: 2.4rem;
  color: #6e6e6e;
`;

function normalizeMonthKey(key) {
  if (!key || typeof key !== 'string') return key;
  const parts = key.split('-');
  const y = parts[0];
  const m = parts[1] || '1';
  return `${y}-${String(m).padStart(2, '0')}`;
}

function buildMonthlyTableFromSummary(monthlyAggregations, surveyId, dateRange) {
  console.log('monthlyAggregations', monthlyAggregations);
  const list = monthlyAggregations[surveyId] || [];
  const currentDate = new Date();
  const allMonths = new Set();
  list.forEach((item) => {
    Object.keys(item.byMonth || {}).forEach((d) => allMonths.add(normalizeMonthKey(d)));
  });
  let datesArray = Array.from(allMonths).sort((a, b) => {
    const [yA, mA] = a.split('-').map(Number);
    const [yB, mB] = b.split('-').map(Number);
    return new Date(yB, (mB || 1) - 1) - new Date(yA, (mA || 1) - 1);
  });
  console.log('datesArray', datesArray);
  if (dateRange[0] && dateRange[1]) {
    const start = moment(dateRange[0]).startOf('month');
    const end = moment(dateRange[1]).endOf('month');
    datesArray = datesArray.filter((date) => {
      const d = moment(date, 'YYYY-MM');
      return d.isBetween(start, end, null, '[]');
    });
  }
  const columns = [
    {
      title: 'Question',
      dataIndex: 'questionTitle',
      key: 'questionTitle',
      fixed: 'left',
      render: (val, row) => row.questionTitle ?? row.question_title ?? val ?? '-',
    },
  ];
  datesArray.forEach((date) => {
    const [year, month] = date.split('-');
    if (currentDate >= new Date(parseInt(year, 10), parseInt(month || 1, 10) - 1)) {
      columns.push({
        title: `${MONTH_NAMES[parseInt(month || 1, 10) - 1]} ${parseInt(year, 10) + THAI_YEAR_OFFSET}`,
        dataIndex: date,
        key: date,
      });
    }
  });
  const dataSourceQuery = list
    .filter((item) => {
      for (const phrase of UNUSED_QUESTION_PHRASES) {
        if (item.questionTitle && item.questionTitle.includes(phrase)) return false;
      }
      return true;
    })
    .map((item) => {
      const questionTitle = item.questionTitle ?? item.question_title ?? '';
      const questionId = item.questionId ?? item.question_id;
      const dataRow = { questionId, questionTitle };
      datesArray.forEach((date) => {
        const rawKey = item.byMonth && Object.keys(item.byMonth).find((k) => normalizeMonthKey(k) === date);
        const cell = rawKey ? item.byMonth[rawKey] : null;
        if (cell) {
          const pct = cell.maximum ? parseFloat(((cell.answer / cell.maximum) * 100).toFixed(2)) : 0;
          dataRow[date] = `จำนวนคน: ${cell.count} และ จำนวนคะแนน ${cell.answer}/${cell.maximum} (${pct}%)`;
        }
      });
      return dataRow;
    });
  console.log('dataSourceQuery', dataSourceQuery);
  return { columns, dataSource: dataSourceQuery };
}

const ReportSubmission = (props) => {
  const {
    surveys = [],
    surveySummaries = [],
    surveySummariesAllTime = [],
    monthlyAggregations = {},
    summaryLoading = false,
    summaryError = null,
    submissions = [],
    submissionError = null,
    submissionTotal = 0,
    submissionPage = 1,
    submissionLimit = 20,
    submissionLoading = false,
    dateRange: dateRangeFromParent = null,
    onRetry,
    onTabChange,
    onPageChange,
    onDateRangeChange,
  } = props;

  const [columns, setColumns] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [filteredDataSource, setFilteredDataSource] = useState([]);
  const [tab, setTab] = useState('0');
  const [viewMode, setViewMode] = useState('table');
  const [localDateRange, setLocalDateRange] = useState([null, null]);

  const dateRange = (dateRangeFromParent && Array.isArray(dateRangeFromParent) && dateRangeFromParent.length >= 2)
    ? dateRangeFromParent
    : localDateRange;

  const rangePickerValue = useMemo(() => {
    const toDayjs = (d) => {
      if (!d) return null;
      if (d.format) return dayjs(d.format('YYYY-MM'), 'YYYY-MM');
      if (d instanceof Date) return dayjs(d).startOf('month');
      return dayjs(d).startOf('month');
    };
    return [toDayjs(dateRange[0]), toDayjs(dateRange[1])];
  }, [
    dateRange[0]?.format?.('YYYY-MM') ?? dateRange[0],
    dateRange[1]?.format?.('YYYY-MM') ?? dateRange[1],
  ]);

  const currentSurveyId = surveys[parseInt(tab, 10)]?.id || null;

  const { columns: columnsAnswer, dataSource: answerDataSource } = useMemo(() => {
    if (!currentSurveyId) return { columns: [], dataSource: [] };
    return buildMonthlyTableFromSummary(monthlyAggregations, currentSurveyId, dateRange);
  }, [monthlyAggregations, currentSurveyId, dateRange]);

  useEffect(() => {
    if (surveys.length > 0 && onTabChange) {
      const survey = surveys[parseInt(tab, 10)];
      if (survey) onTabChange(survey.id);
    }
  }, [surveys.length]);

  useEffect(() => {
    if (surveys.length === 0 || submissions.length === 0) {
      setColumns([]);
      setDataSource([]);
      setFilteredDataSource([]);
      return;
    }
    const survey = surveys[parseInt(tab, 10)];
    if (!survey) return;

    const filterSubmissions = submissions.filter((s) => s.surveyId === survey.id);
    if (filterSubmissions.length === 0) {
      setColumns([]);
      setDataSource([]);
      setFilteredDataSource([]);
      return;
    }

    const withAnswers = filterSubmissions.filter((s) => Array.isArray(s.answers) && s.answers.length > 0);
    const firstWithAnswers = withAnswers[0] || filterSubmissions[0];

    const filterColumns = (firstWithAnswers.survey?.questions || []).map((q) => ({
      title: q.text || 'Question',
      key: `q-${q.id}`,
      dataIndex: q.id,
      align: 'center',
      render: (_, record) => {
        const rec = Array.isArray(record) ? record.filter((r) => r.questionId === q.id) : [];
        return rec.length > 0 ? rec[0].answer : '';
      },
    }));
    setColumns(filterColumns);

    // ใช้เฉพาะรายการที่ API ส่งมา (API กรอง startDate/endDate อยู่แล้ว) ไม่กรองวันที่ซ้ำบน frontend
    const list = withAnswers.length > 0 ? withAnswers : filterSubmissions;

    const tableData = list.map((sub) =>
      (Array.isArray(sub.answers) ? sub.answers : []).map((ans) => {
        const ids = (ans.selectedOption || []).map((o) => (o && o.id) || o);
        const labels = (ans.question?.options || []).filter((o) => ids.includes(o.id)).map((o) => o.label);
        return {
          questionTitle: ans.question?.text,
          questionId: ans.questionId,
          answer: labels.join(', '),
          createdAt: sub.createdAt,
        };
      })
    );

    if (dateRange[0] && dateRange[1]) {
      setFilteredDataSource(tableData);
      setDataSource([]);
    } else {
      setDataSource(tableData);
      setFilteredDataSource([]);
    }
  }, [submissions, surveys, tab, dateRange]);

  const handleTabChange = (key) => {
    setTab(key);
    const survey = surveys[parseInt(key, 10)];
    if (survey && onTabChange) onTabChange(survey.id);
  };

  const handleDateChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      if (!dateRangeFromParent) setLocalDateRange([null, null]);
      onDateRangeChange && onDateRangeChange(null, null);
      return;
    }
    const d0 = dates[0].format ? dates[0].format('YYYY-MM') : null;
    const d1 = dates[1].format ? dates[1].format('YYYY-MM') : null;
    if (!d0 || !d1) return;
    let start = moment(d0, 'YYYY-MM').startOf('month');
    let end = moment(d1, 'YYYY-MM').endOf('month');
    const fromParent = dateRangeFromParent && dateRangeFromParent[0] && dateRangeFromParent[1];
    const parentStart = fromParent && moment(dateRangeFromParent[0]).format('YYYY-MM');
    if (fromParent && d0 === d1 && parentStart !== d0) {
      start = moment(dateRangeFromParent[0]).startOf('month');
    }
    if (!dateRangeFromParent) setLocalDateRange([start, end]);
    onDateRangeChange && onDateRangeChange(start, end);
  };

  /** จำนวนฟอร์ม: แสดงยอดรวมทุกปี (surveySummariesAllTime) ถ้ามี ไม่ใช่แค่ช่วงที่เลือก */
  const getCountForSurvey = (survey) => {
    const sAll = surveySummariesAllTime.find((x) => x.surveyId === survey.id);
    if (sAll != null && (sAll.count !== undefined || sAll.totalCount !== undefined)) {
      return sAll.count ?? sAll.totalCount ?? 0;
    }
    const s = surveySummaries.find((x) => x.surveyId === survey.id);
    return s ? s.count : 0;
  };

  const disabledDate = (current) => current && current > dayjs().endOf('month');

  const isEmpty = !summaryError && !submissionError && surveys.length === 0 && surveySummaries.length === 0;
  const hasError = Boolean(summaryError || submissionError);

  if (summaryLoading && surveySummaries.length === 0) {
    return (
      <div className="h-100 w-100 d-flex justify-content-center align-items-center">
        <Spin className="text-center" />
      </div>
    );
  }

  return (
    <div className="h-100">
      {isEmpty && !hasError && (
        <Empty description="ยังไม่มีข้อมูลแบบสอบถาม" style={{ padding: 48 }} />
      )}
      {hasError && (
        <div className="mb-2">
          <Button type="primary" onClick={onRetry}>โหลดใหม่</Button>
        </div>
      )}
      {surveys.length > 0 && (
        <Row>
          {surveys.map((survey, index) => (
            <DataCard key={survey.id} className="ml-3 mr-3 mt-2 mb-2 pt-1 pb-1" bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <View>
                <Title className="pb-1">{survey.title}</Title>
                <View className="pt-2" style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'center' }}>
                  <Description style={{ margin: 0 }}>{getCountForSurvey(survey)}</Description>
                  <Description style={{ fontSize: 14 }}>ฟอร์ม</Description>
                </View>
              </View>
            </DataCard>
          ))}

          <Tabs
            style={{ width: '100%' }}
            activeKey={tab}
            onChange={handleTabChange}
            destroyInactiveTabPane
          >
            {surveys.map((survey, surveyIndex) => (
              <Tabs.TabPane tab={survey.title} key={String(surveyIndex)}>
                <Button onClick={() => setViewMode((m) => (m === 'table' ? 'chart' : 'table'))}>
                  {viewMode === 'table' ? 'เปลี่ยนเป็นมุมมองกราฟ' : 'เปลี่ยนเป็นมุมมองตาราง'}
                </Button>
                <RangePicker
                  format="YYYY-MM"
                  picker="month"
                  value={rangePickerValue}
                  disabledDate={disabledDate}
                  onChange={handleDateChange}
                  allowClear
                  style={{ marginBottom: '20px', marginLeft: 8 }}
                />
                {viewMode === 'table' ? (
                  <>
                    <Table
                      rowKey={(_, index) => `submission-row-${index}`}
                      columns={columns}
                      dataSource={filteredDataSource.length > 0 ? filteredDataSource : dataSource}
                      scroll={{ x: 'max-content' }}
                      loading={submissionLoading}
                      pagination={false}
                    />
                    {onPageChange && (
                      <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Pagination
                          current={submissionPage}
                          pageSize={submissionLimit}
                          total={submissionTotal}
                          showSizeChanger
                          showTotal={(total) => `ทั้งหมด ${total} รายการ`}
                          onChange={(page, pageSize) => onPageChange(page, pageSize || submissionLimit)}
                        />
                      </div>
                    )}
                    <Table
                      rowKey={(row) => row.questionId ?? row.question_id ?? Math.random()}
                      columns={columnsAnswer}
                      dataSource={answerDataSource}
                      scroll={{ x: 'max-content' }}
                      pagination={false}
                    />
                  </>
                ) : (
                  <SurveyBarChart dataSource={answerDataSource} />
                )}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </Row>
      )}
    </div>
  );
};

export default ReportSubmission;
